import { Container, Sprite, Texture } from 'pixi.js';
import type { Game } from './Game';
import { loadBulletGfx, type EmitterConfig } from './objects/ObjectGraphics';
import { VIEW_H, VIEW_W } from '../render/AreaView';

// MMF2 → engine scaling (BaseBullet.cs).
const VELOCITY_SCALE = 50 / 8;
const GRAVITY_SCALE = VELOCITY_SCALE * VELOCITY_SCALE;
const DECELERATION_SCALE = 0.05;

// BulletLayer.cs bulletsLimit: per-scene live-bullet caps. At the limit, the OLDEST
// bullet of that type (live or not) is recycled for the new shot.
const BULLET_LIMITS: Record<string, number> = {
  CauldronSpike: 20,
  ShockWave: 100,
  DropStuff: 500,
  RollBullet: 15,
  BlueBullet: 2,
  NinjaStar: 15,
  HomingBullet: 15,
  BigGlowingBullet: 10,
  FireBullet2: 15,
  FireBullet: 150,
  FireBulletLite: 150,
  EvilFlowerBullet: 150,
  SmallGlowingBullet: 200,
  BlueBulletExplosion: 100,
  ShockWave2: 40,
  SuperBullet: 100,
  RunnerBullet: 15,
  BeeBullet: 15,
  GhostSlimeBullet: 50,
  FlythingBullet: 40,
};
// Every registered scene is in the C# table (a missing key would throw upstream);
// this default only guards against a future extractor addition.
const DEFAULT_BULLET_LIMIT = 100;

interface BulletFrames {
  frames: Texture[];
  fps: number;
  w: number;
  h: number;
  // BaseBullet.disappear(collide:true): the scene's "disappear" animation + HitPlayer
  // sound, played on solid impact (not on area-exit, where collide is false).
  disappear?: { frames: Texture[]; fps: number };
  hitSound?: string;
}

// BigGlowingBullet.disappear(collide) / BlueBullet.disappear(collide): on hitting a
// solid, burst N SmallGlowingBullet/BlueBulletExplosion bullets radially (DirectionMMF2=i,
// BigGlowing signs it by which half of the circle it was travelling in).
const BURST_ON_IMPACT: Record<string, { bullet: string; count: number; signed: boolean }> = {
  BigGlowingBullet: { bullet: 'SmallGlowingBullet', count: 17, signed: true },
  BlueBullet: { bullet: 'BlueBulletExplosion', count: 32, signed: false },
};
// Per-type CollisionShape2D offset from the spawn node (the .tscn's shape position).
// RollBullet: circle at (0.5, 16) — the ball's physics centre is 16px below the
// emitter line, so it rolls along the floor instead of gliding mid-tile.
const SHAPE_OFFSET: Record<string, [number, number]> = {
  RollBullet: [0.5, 16],
  CauldronSpike: [1, 0.5],
};
// Sprite-centre offset from the physics centre, from the .tscn's AnimatedSprite node.
// RollBullet: sprite at (0,-0.5), offset (-12,0), non-centered → visual centre (0,11.5)
// vs shape centre (0.5,16) — the art rides 4.5px higher, so its bottom edge (23.5)
// kisses the floor exactly like the collision circle's bottom (16+7.5).
const SPRITE_OFFSET: Record<string, [number, number]> = {
  RollBullet: [-0.5, -4.5],
  CauldronSpike: [-1, -0.5],
};
// SuperBullet.onDisappear: population culling by same-type count — 1/count chance per
// tick above `limit` (0 via the default-1s SlowDisappearTimer, 18 via the 0.06s Fast one).
// Bullets whose .tscn collision_mask includes the umbrella layer (4096) die against the
// deployed canopy (BaseBullet MoveAndCollide vs Juni.tscn's UmbrellaBody). Masks read
// from each bullet scene: 6176/6144 → blocked; 2048/2080 (RollBullet, BlueBullet family,
// ShockWave, SmallGlowingBullet, CauldronSpike, FireBullet2 via BaseBullet) pass through.
const UMBRELLA_BLOCKED = new Set([
  'BeeBullet',
  'DropStuff',
  'EvilFlowerBullet',
  'FireBullet',
  'FlythingBullet',
  'GhostSlimeBullet',
  'HomingBullet',
  'NinjaStar',
  'RunnerBullet',
  'ShockWave2',
  'SuperBullet',
]);
const SUPER_BULLET_CULL = [
  { interval: 1, limit: 0 },
  { interval: 0.06, limit: 18 },
];

class Bullet {
  sprite: Sprite;
  active = false;
  x = 0;
  y = 0;
  vx = 0;
  vy = 0;
  gravity = 0;
  decelX = 0;
  decelY = 0;
  pinX = 1;
  pinUp = 1;
  pinDown = 1;
  disappearStopped = false;
  homing = false;
  // Visual offset from the physics centre (SPRITE_OFFSET), applied wherever sprite.x/y is set.
  private sprOffX = 0;
  private sprOffY = 0;
  // Playing the one-shot "disappear" animation after a solid impact (no longer lethal).
  private dying = false;
  private disappear: { frames: Texture[]; fps: number } | null = null;
  private hitSound: string | null = null;
  // False while the bullet still overlaps solid at spawn (e.g. RollBullet emerging from
  // the RollerGenerator's own solid box — in Godot the box's StaticBody layer isn't on
  // the bullet's mask, but the port's CollisionMap merges object stamps with terrain).
  private armed = true;
  bulletType = '';
  // Overlay-neutralized emitters fire safe bullets that can't kill Juni
  // (GDObjectLayers.cs SafeBullets → BaseBullet.Safe).
  safe = false;
  // Render z of this bullet (emitter's object-layer z − 1, BulletLayer.cs:79);
  // impact sub-bursts inherit it (their emitter is the bullet itself upstream).
  zLayer = 11;
  w = 8;
  h = 8;
  private frames: Texture[] = [];
  private fps = 8;
  private animTime = 0;
  private animIndex = 0;
  // SmallGlowingBullet: VelocityMMF2 += 20/s while alive (accelerates along its
  // current heading — the MMF2 field is a scalar speed, not per-axis).
  private accelerating = false;
  private wobble = 0;
  private cullTimers = [0, 0];
  private manager: BulletManager | null = null;

  constructor() {
    this.sprite = new Sprite();
    this.sprite.anchor.set(0.5, 0.5);
    this.sprite.visible = false;
  }

  /** Still on screen (live or playing its disappear flourish) — BulletLayer's Visible check. */
  get inUse(): boolean {
    return this.active || this.dying;
  }

  configure(
    cfg: EmitterConfig,
    gfx: BulletFrames,
    originX: number,
    originY: number,
    manager: BulletManager,
    counter = 0,
    velOverride?: number,
    rawDir?: number,
  ): void {
    this.frames = gfx.frames;
    this.fps = gfx.fps;
    this.w = gfx.w;
    this.h = gfx.h;
    this.sprite.texture = gfx.frames[0] ?? Texture.WHITE;
    this.animTime = 0;
    this.animIndex = 0;
    this.disappearStopped = cfg.disappearStopped ?? false;
    if (this.active && this.manager) this.manager.trackActive(this.bulletType, -1); // recycled while live
    this.bulletType = cfg.bullet;
    this.manager = manager;
    manager.trackActive(cfg.bullet, 1);
    this.cullTimers = [1, 0.06]; // SuperBullet's Slow/FastDisappearTimer intervals
    const burstType = BURST_ON_IMPACT[cfg.bullet]?.bullet;
    if (burstType) manager.preload(burstType);
    // True homing (HomingBullet.cs): accelerates toward Juni each frame mid-flight.
    this.homing = cfg.bullet === 'HomingBullet';
    // SmallGlowingBullet.cs: VelocityMMF2 += delta*20 every tick (accelerates along heading).
    this.accelerating = cfg.bullet === 'SmallGlowingBullet';
    // FireBullet2.cs: Direction += (rand(3)-1)*0.9*delta — a drifting flame wobble.
    this.wobble = cfg.bullet === 'FireBullet2' ? 0.9 : 0;
    this.armed = false; // arms on the first tick it stands clear of solid
    const sprOff = SPRITE_OFFSET[cfg.bullet];
    this.sprOffX = sprOff ? sprOff[0] : 0;
    this.sprOffY = sprOff ? sprOff[1] : 0;
    this.dying = false;
    this.disappear = gfx.disappear ?? null;
    this.hitSound = gfx.hitSound ?? null;

    if (cfg.cartesian) {
      // Direct px/s velocity (UpCannon/WaterMonster): pick per-axis, no MMF2/decel.
      const c = cfg.cartesian;
      const pick = (r: number[], discrete: boolean) =>
        discrete ? (Math.random() < 0.5 ? r[0] : r[1]) : r[0] + Math.random() * (r[1] - r[0]);
      this.vx = pick(c.vx, c.vxD);
      this.vy = pick(c.vy, c.vyD);
      this.gravity = c.gravity;
      this.decelX = this.decelY = 0;
      this.pinX = this.pinUp = this.pinDown = 1;
      const so = SHAPE_OFFSET[cfg.bullet];
      this.x = originX + cfg.tx + (so ? so[0] : 0);
      this.y = originY + cfg.ty + (so ? so[1] : 0);
      this.active = true;
      this.sprite.visible = true;
      this.sprite.x = this.x + this.sprOffX;
      this.sprite.y = this.y + this.sprOffY;
      return;
    }

    const vel = ((velOverride ?? cfg.vel) + counter * (cfg.velPerI ?? 0)) * VELOCITY_SCALE;
    // ShockDisk: DirectionMMF2 = i → each burst bullet fans out one MMF2 direction step.
    // dirTable (BadFlything/HomingFlything): each burst index takes its own MMF2 direction.
    const mmf2 = cfg.dirTable ? cfg.dirTable[counter % cfg.dirTable.length] : cfg.dirPerI ? counter : cfg.dir;
    const dir = (1 - mmf2 / 16) * Math.PI;
    this.gravity = cfg.gravity * GRAVITY_SCALE;
    const decel = cfg.decel * DECELERATION_SCALE;
    if (rawDir != null) {
      // Raw Godot angle: BaseBullet negates (V·-cosθ, V·-sinθ) — TrapFire's atan2 from
      // Juni through the trap therefore fires *toward* Juni.
      this.vx = vel * -Math.cos(rawDir);
      this.vy = vel * -Math.sin(rawDir);
    } else {
      this.vx = vel * -Math.cos(dir);
      this.vy = vel * -Math.sin(dir);
    }
    this.decelX = this.vx * decel;
    this.decelY = Math.abs(this.vy * decel);
    if (cfg.pinball) {
      this.pinX = 0.78;
      this.pinUp = 0.5;
      this.pinDown = 0.5;
    } else {
      this.pinX = this.pinUp = this.pinDown = 1;
    }
    const shapeOff = SHAPE_OFFSET[cfg.bullet];
    this.x = originX + cfg.tx + (shapeOff ? shapeOff[0] : 0);
    this.y = originY + cfg.ty + (shapeOff ? shapeOff[1] : 0);
    this.active = true;
    this.sprite.visible = true;
    this.sprite.x = this.x + this.sprOffX;
    this.sprite.y = this.y + this.sprOffY;
  }

  deactivate(): void {
    if (this.active && this.manager) this.manager.trackActive(this.bulletType, -1);
    this.active = false;
    this.dying = false;
    this.sprite.visible = false;
  }

  /** Solid-impact death: HitPlayer sound + one-shot "disappear" animation, then hide. */
  private die(game: Game): void {
    if (this.hitSound) game.sfxAt(this.hitSound, this.x, this.y);
    if (this.disappear && this.disappear.frames.length) {
      if (this.active && this.manager) this.manager.trackActive(this.bulletType, -1);
      this.active = false; // no longer moves or kills, but keeps animating
      this.dying = true;
      this.animTime = 0;
      this.animIndex = 0;
      this.sprite.texture = this.disappear.frames[0];
    } else {
      this.deactivate();
    }
  }

  update(dt: number, game: Game): void {
    if (this.dying) {
      const dis = this.disappear!;
      this.animTime += dt;
      const step = 1 / dis.fps;
      while (this.animTime >= step && this.dying) {
        this.animTime -= step;
        if (++this.animIndex >= dis.frames.length) {
          this.dying = false;
          this.sprite.visible = false;
        } else {
          this.sprite.texture = dis.frames[this.animIndex];
        }
      }
      return;
    }
    if (!this.active) return;

    // HomingBullet: nudge velocity toward Juni's apparent position (2.5 px/frame @60fps).
    if (this.homing) {
      const p = game.player;
      this.vx += 150 * dt * (p.apparentX < this.x ? -1 : 1);
      this.vy += 150 * dt * (p.apparentY < this.y ? -1 : 1);
    }

    this.vx -= this.decelX * dt * this.pinX;
    if (this.decelX > 0 && this.vx < 0) this.vx = 0;
    if (this.decelX < 0 && this.vx > 0) this.vx = 0;
    if (this.vy > 0) {
      this.vy -= this.decelY * dt * this.pinDown;
      if (this.gravity === 0 && this.vy < 0) this.vy = 0;
    } else if (this.vy < 0) {
      this.vy += this.decelY * dt * this.pinUp;
      if (this.gravity === 0 && this.vy > 0) this.vy = 0;
    }
    this.vy += this.gravity * dt;

    // FireBullet2.cs: direction wobbles by (rand(3)-1)*0.9*dt — rotate the velocity vector.
    if (this.wobble) {
      const a = (Math.floor(Math.random() * 3) - 1) * this.wobble * dt;
      if (a !== 0) {
        const cos = Math.cos(a);
        const sin = Math.sin(a);
        const nvx = this.vx * cos - this.vy * sin;
        this.vy = this.vx * sin + this.vy * cos;
        this.vx = nvx;
      }
    }

    // SmallGlowingBullet.cs: VelocityMMF2 += delta*20 — accelerate along the current heading.
    if (this.accelerating) {
      const speed = Math.hypot(this.vx, this.vy);
      if (speed > 0) {
        const boost = 1 + (20 * VELOCITY_SCALE * dt) / speed;
        this.vx *= boost;
        this.vy *= boost;
      }
    }

    // DisappearWhenStopped (ShockWave): vanish once decelerated to a halt.
    if (this.disappearStopped && this.gravity === 0 && this.vx === 0 && this.vy === 0) {
      this.deactivate();
      return;
    }

    // SuperBullet.cs: population culling — 1/count chance per tick of despawning once
    // the same-type population exceeds `limit` (checked at two different tick rates).
    const burst = BURST_ON_IMPACT[this.bulletType];
    if (this.bulletType === 'SuperBullet' && this.manager) {
      for (let i = 0; i < SUPER_BULLET_CULL.length; i++) {
        this.cullTimers[i] -= dt;
        if (this.cullTimers[i] > 0) continue;
        this.cullTimers[i] += SUPER_BULLET_CULL[i].interval;
        const count = this.manager.countActive('SuperBullet');
        if (count > SUPER_BULLET_CULL[i].limit && Math.floor(Math.random() * count) === 0) {
          this.deactivate();
          return;
        }
      }
      // SuperBullet.cs: a small random positional jitter every physics tick.
      this.x += (Math.floor(Math.random() * 3) - 1) * dt * 30;
      this.y += (Math.floor(Math.random() * 3) - 1) * dt * 30;
    }

    this.x += this.vx * dt;
    this.y += this.vy * dt;
    this.sprite.x = Math.round(this.x + this.sprOffX);
    this.sprite.y = Math.round(this.y + this.sprOffY);

    // Leaving the current screen, or hitting solid → vanish (bursting a sub-explosion first).
    const lx = this.x - game.areaOriginX;
    const ly = this.y - game.areaOriginY;
    if (lx < 2 || lx > VIEW_W - 2 || ly < 2 || ly > VIEW_H - 2) {
      this.deactivate();
      return;
    }
    if (!this.armed) {
      if (game.collision.solidAt(this.x, this.y)) return; // still emerging from its emitter's solid stamp
      this.armed = true;
    }
    if (game.collision.solidAt(this.x, this.y)) {
      if (burst && this.manager) {
        // BigGlowingBullet: sign the fan by which half of the circle it was travelling
        // in (Direction < Pi is the upper half in Godot's convention; approximated here
        // from the bullet's own heading angle since we don't track raw Godot radians).
        const angle = ((Math.atan2(this.vy, this.vx) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        const sign = burst.signed ? (angle < Math.PI ? -1 : 1) : 1;
        for (let i = 0; i < burst.count; i++) {
          this.manager.emit(
            {
              bullet: burst.bullet,
              tx: 0,
              ty: burst.signed ? -sign * 2 : 0,
              dir: sign * i,
              vel: 5,
              gravity: 0,
              decel: burst.signed ? 0 : 15,
              pinball: false,
              interval: 0,
              disappearStopped: !burst.signed,
            },
            this.x,
            this.y,
            0,
            this.safe, // impact burst inherits the parent's safety
            this.zLayer, // and its z (upstream: emitter is the bullet, parent = BulletLayer)
          );
        }
      }
      this.die(game);
      return;
    }
    // LiquidPoolDeadly.cs: bullets entering a deadly pool's wave band disappear —
    // GhostSlimeBullet with its collide flourish (sound + anim), others silently.
    if (game.objectLayers?.deadlyPoolAt(this.x, this.y)) {
      if (this.bulletType === 'GhostSlimeBullet') this.die(game);
      else this.deactivate();
      return;
    }
    // Umbrella shield: 4096-mask bullets die against the open canopy with their
    // collide flourish, exactly like a solid impact (safe bullets still collide).
    if (
      UMBRELLA_BLOCKED.has(this.bulletType) &&
      game.player.umbrellaBlocksRect(this.x - this.w / 2, this.y - this.h / 2, this.w, this.h)
    ) {
      this.die(game);
      return;
    }
    // Kill Juni on contact — bullets collide with her narrow Hitbox Area2D (6x16.6),
    // not the wider physics body that object/hazard areas use.
    if (!this.safe && !game.player.dead && game.player.overlapsBulletRect(this.x - this.w / 2, this.y - this.h / 2, this.w, this.h)) {
      game.player.die();
    }

    if (this.frames.length > 1) {
      this.animTime += dt;
      const step = 1 / this.fps;
      while (this.animTime >= step) {
        this.animTime -= step;
        this.animIndex = (this.animIndex + 1) % this.frames.length;
        this.sprite.texture = this.frames[this.animIndex];
      }
    }
  }
}

/** Manages all active bullets for the current area. */
export class BulletManager {
  // Per-type FIFO queues (BulletLayer.cs `bullets`): oldest-first, recycled at the cap.
  private queues = new Map<string, Bullet[]>();
  private allBullets: Bullet[] = [];
  private gfxCache = new Map<string, BulletFrames | null>();
  private activeCounts = new Map<string, number>();
  // One container per render z (bullet z = emitter object-layer z − 1, so layer-0/1
  // bullets render below Juni @ z8 and layer-2/3 bullets above), lazily attached to
  // the sortable worldLayer.
  private zContainers = new Map<number, Container>();

  constructor(private game: Game) {}

  private containerFor(z: number): Container {
    let c = this.zContainers.get(z);
    if (!c) {
      c = new Container();
      c.zIndex = z;
      this.zContainers.set(z, c);
      this.game.worldLayer.addChild(c);
    }
    return c;
  }

  /** Ensure a bullet type's frames are loaded (async, cached). */
  preload(type: string): void {
    if (this.gfxCache.has(type)) return;
    this.gfxCache.set(type, null);
    void loadBulletGfx(this.game, type).then((g) => this.gfxCache.set(type, g));
  }

  /** Are a bullet type's frames loaded and ready to emit? */
  isLoaded(type: string): boolean {
    return !!this.gfxCache.get(type);
  }

  /** Active bullets of a given type (SuperBullet.cs's Selector.GetSize(by_type)). */
  countActive(type: string): number {
    return this.activeCounts.get(type) ?? 0;
  }

  /** Bullet lifecycle bookkeeping so countActive is O(1) (SuperBullet polls it per tick). */
  trackActive(type: string, delta: number): void {
    this.activeCounts.set(type, (this.activeCounts.get(type) ?? 0) + delta);
  }

  emit(cfg: EmitterConfig, originX: number, originY: number, counter = 0, safe = false, z = 11): void {
    const gfx = this.gfxCache.get(cfg.bullet);
    if (!gfx) {
      this.preload(cfg.bullet);
      return; // frames not ready this frame; fires once loaded
    }
    // BulletLayer.cs _emit: at the per-type cap, recycle the OLDEST bullet (even if
    // live); below it, reuse the queue head only if it's already off-screen.
    let queue = this.queues.get(cfg.bullet);
    if (!queue) {
      queue = [];
      this.queues.set(cfg.bullet, queue);
    }
    const limit = BULLET_LIMITS[cfg.bullet] ?? DEFAULT_BULLET_LIMIT;
    let bullet: Bullet;
    if (queue.length >= limit) {
      bullet = queue.shift()!;
    } else if (queue.length === 0 || queue[0].inUse) {
      bullet = new Bullet();
      this.allBullets.push(bullet);
    } else {
      bullet = queue.shift()!;
    }
    queue.push(bullet);
    bullet.zLayer = z;
    const parent = this.containerFor(z);
    if (bullet.sprite.parent !== parent) parent.addChild(bullet.sprite);
    // HomingCannon/DarkHomingCannon: fire velocity leads Juni's horizontal distance.
    let velOverride: number | undefined;
    if (cfg.aimX) {
      const cannonX = originX + 12; // object centre (yknytt Center.x)
      velOverride =
        Math.max(0, cannonX - this.game.player.apparentX) / cfg.aimX.divisor +
        cfg.aimX.base +
        Math.floor(Math.random() * cfg.aimX.rand);
    }
    // TrapFire: the raw Godot angle from Juni (+10 y bias) through the object —
    // BaseBullet's negation makes the bullet fly toward Juni. StuffShooter uses a
    // fixed raw angle (straight up/down).
    let rawDir: number | undefined;
    if (cfg.fixedRawDir != null) {
      rawDir = cfg.fixedRawDir;
    } else if (cfg.aimAway) {
      rawDir = Math.atan2(
        originY - this.game.player.apparentY + 10,
        originX - this.game.player.apparentX,
      );
    }
    bullet.configure(cfg, gfx, originX, originY, this, counter, velOverride, rawDir);
    bullet.safe = safe;
  }

  update(dt: number): void {
    for (const b of this.allBullets) b.update(dt, this.game);
  }

  clear(): void {
    for (const b of this.allBullets) b.deactivate();
  }

  destroy(): void {
    this.clear();
    for (const c of this.zContainers.values()) c.destroy({ children: true });
    this.zContainers.clear();
    this.queues.clear();
    this.allBullets = [];
  }
}
